import {
  ColorClass,
  Icon,
  IconIdentifier,
  Input,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import { useEffect, useRef } from 'react';
import { HeapMessageLevel } from 'shared/api';
import { OverflowTextTooltip } from 'shared/components';
import { useInputFields } from 'shared/hooks';
import type { HeapNameItemProps } from './types';

const getPolygonIconColorClass = (
  messageLevel: HeapMessageLevel | null,
): ColorClass => {
  switch (messageLevel) {
    case HeapMessageLevel.Warning:
      return ColorClass.Warning500;
    case HeapMessageLevel.Error:
      return ColorClass.Error600;
    default:
      return ColorClass.Primary500;
  }
};

export const HeapNameItem: React.FC<HeapNameItemProps> = ({
  heapName,
  heapId,
  message,
  messageLevel,
  handleRenameHeap,
  editingHeapId,
  setEditingHeapId,
}) => {
  const isEditingHeapName = editingHeapId === heapId;

  // useInputFields.
  const { values, names, onChange, onBlur, onFocus } = useInputFields({
    heapName: heapName,
  });

  // useRefs.
  const inputRef = useRef<HTMLInputElement | null>(null);

  // useEffect.
  useEffect(() => {
    if (inputRef.current && isEditingHeapName) {
      inputRef.current.focus();

      const handleClickOutside = (event: MouseEvent) => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target as Node)
        ) {
          handleRenameHeap(heapId, values.heapName);
          setEditingHeapId(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditingHeapName, values.heapName]);

  // Handlers.
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      handleRenameHeap(heapId, values.heapName);
      setEditingHeapId(null);
    }
  };

  // Variables.
  const polygonIconColorClass = getPolygonIconColorClass(messageLevel);
  const isCalculationFailed = messageLevel === HeapMessageLevel.Error;

  return !isEditingHeapName ? (
    <div className="value-cell heap-name-value">
      <Icon
        identifier={IconIdentifier.Polygon}
        size={18}
        colorClass={polygonIconColorClass}
      />
      {message ? (
        <Tooltip hoverText={message} placement={Placement.Top}>
          <div className="heap-name-value__error">{heapName}</div>
        </Tooltip>
      ) : (
        <OverflowTextTooltip
          text={`${heapName}`}
          maxLength={27}
          tooltipPlacement={Placement.Top}
          className="heap-name-value__text"
        />
      )}
      {!isCalculationFailed && (
        <Icon
          identifier={IconIdentifier.Pencil}
          size={18}
          colorClass={ColorClass.Neutral250}
          onClick={() => setEditingHeapId(heapId)}
        />
      )}
    </div>
  ) : (
    <Input.Text
      value={values.heapName}
      ref={inputRef}
      name={names.heapName}
      className="heap-name-value__input"
      onKeyDown={(event) => handleOnKeyDown(event)}
      {...{ onChange, onBlur, onFocus }}
    />
  );
};
